import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { dpsSend } from 'src/bff';
import { FormController, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import {
  checkUndefinedsInData,
  formatBooleanField,
  formatRatingField,
  handleDescWrongData,
} from './helpers';
import { CEMLegal } from './CEMLegal';
import { CustomLegalCem } from './CustomLegalCem';
import { CemFormProps } from './CemController';
import { formatHeaders, formDataBuilder } from '../format-fields';
import {
  addRemovedStep,
  getArrayOfStepsNames,
  getIndexInSteps,
  removeMultipleStepsAndResetScreenIndex,
  resetStepsScreenIndex
} from '../helpers/add-remove-steps';
import { stepsPosventa } from './CemStepsPosventa';
import { ThankYouCEM } from './ThankYouCem';

const betweenOneAndThreeStars = (rating: string) => Number(rating) !== 0 && Number(rating) < 4;
const oneOrTwoStars = (rating: string) => Number(rating) !== 0 && Number(rating) < 3;
const fourOrFiveStars = (rating: string) => Number(rating) !== 0 && Number(rating) >= 4 && Number(rating) < 6;
const noRatingStars = (rating: string) => rating === 'Sin respuesta';

// Customers that give 5 or 4 stars in Sc.7
// and evaluated less than all 3 questions of Sc.5, Sc.6 and Sc.6 with dissatisfaction (codes 3,2,1)
const satisfaccionConcesionFirstRequisite = (
  satisfaccionConcesion: string,
  satisfaccionTrato: string,
  satisfaccionOrganizacion: string,
  satisfaccionTrabajoRealizado: string
) =>
  fourOrFiveStars(satisfaccionConcesion) &&
  (!betweenOneAndThreeStars(satisfaccionTrato) ||
    !betweenOneAndThreeStars(satisfaccionOrganizacion) ||
    !betweenOneAndThreeStars(satisfaccionTrabajoRealizado));

//Customers that give 3, 2 or 1 stars in Sc.7
// and give 5 or 4 stars or chose “no answer” in Sc.4 to Sc.6

const satisfaccionConcesionSecondRequisite = (
  satisfaccionConcesion: string,
  satisfaccionTrato: string,
  satisfaccionOrganizacion: string,
  satisfaccionTrabajoRealizado: string
) =>
  betweenOneAndThreeStars(satisfaccionConcesion) &&
  (fourOrFiveStars(satisfaccionTrato) || noRatingStars(satisfaccionTrato)) &&
  (fourOrFiveStars(satisfaccionOrganizacion) || noRatingStars(satisfaccionOrganizacion)) &&
  (fourOrFiveStars(satisfaccionTrabajoRealizado) || noRatingStars(satisfaccionTrabajoRealizado));

const showSatisfaccionConcesion = (
  satisfaccionConcesion: string,
  satisfaccionTrato: string,
  satisfaccionOrganizacion: string,
  satisfaccionTrabajoRealizado: string
) =>
  satisfaccionConcesionFirstRequisite(
    satisfaccionConcesion,
    satisfaccionTrato,
    satisfaccionOrganizacion,
    satisfaccionTrabajoRealizado
  ) ||
  satisfaccionConcesionSecondRequisite(
    satisfaccionConcesion,
    satisfaccionTrato,
    satisfaccionOrganizacion,
    satisfaccionTrabajoRealizado
  );

// Customers that give 3, 2 or 1 stars in Sc.7
const otroAspectoFirstRequisite = (satisfaccionConcesion: string) => betweenOneAndThreeStars(satisfaccionConcesion);

//evaluated neither 0 nor 3 (i.e. 1 or 2) questions of Sc.4, Sc.5 and Sc.6 with dissatisfaction (codes 3,2,1)
const otroAspectoSecondRequisite = (
  satisfaccionTrato: string,
  satisfaccionOrganizacion: string,
  satisfaccionTrabajoRealizado: string
) => {
  if (
    (betweenOneAndThreeStars(satisfaccionTrato) &&
      betweenOneAndThreeStars(satisfaccionOrganizacion) &&
      !betweenOneAndThreeStars(satisfaccionTrabajoRealizado)) ||
    (betweenOneAndThreeStars(satisfaccionTrato) &&
      !betweenOneAndThreeStars(satisfaccionOrganizacion) &&
      betweenOneAndThreeStars(satisfaccionTrabajoRealizado)) ||
    (!betweenOneAndThreeStars(satisfaccionTrato) &&
      betweenOneAndThreeStars(satisfaccionOrganizacion) &&
      betweenOneAndThreeStars(satisfaccionTrabajoRealizado))
  ) {
    return true;
  }
  return false;
};

const showOtroAspecto = (
  satisfaccionConcesion: string,
  satisfaccionTrato: string,
  satisfaccionOrganizacion: string,
  satisfaccionTrabajoRealizado: string
) =>
  otroAspectoFirstRequisite(satisfaccionConcesion) &&
  otroAspectoSecondRequisite(satisfaccionTrato, satisfaccionOrganizacion, satisfaccionTrabajoRealizado);

export function CemPosventa({ idPerson, idOrder, idVehicle, dpsData, dealerName, dealerGet }: CemFormProps) {
  const { initializeForm, handleNextStep, handleShowFinalScreen } = useOneFormContext();
  const { formData } = useSelector((state: OneFormState) => state);
  const store = useStore();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const test = urlParams.get('test');
  const adviser = urlParams.get('adviser');
  const {
    datosCorrectos,
    datosIncorrectosPosventa,
    satisfaccionTrato,
    satisfaccionOrganizacion,
    satisfaccionTrabajoRealizado,
    satisfaccionConcesion,
    modelo,
    enviarConcesion,
    masPreguntas,
  } = formData.fields;

  const modeloRef = useRef(null);

  // Necesitamos almacenar el modelo ya que cada vez que el usuario selecciona un modelo se modifican los steps, por lo que necesitamos una referencia para saber que modelo hay que sustituir.

  const NuevoFormData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW',
      DESC_CAMPAIGNBLOCK: 'B19',
      DESC_FORM_DETAIL: 'CEM Posventa',
      DESC_FORMOBJECTIVE: 'Encuesta',
      DESC_GDPR_ADAPTED: 'true',
      DESC_ID_CAMPAIGN: '7011n000001EIK1AAO',
      DESC_TOKEN: test ? 'VW_DDB_FRM_CEM_PV_2023_TEST_INSATISFECHOS' : 'VW_DDB_FRM_CEM_PV_2023',
      DESC_WAID_LITERA: 'B09_LCV_CEM_Venta_PV',
    },
    fields: {
      lssiPincode: idPerson.LSSI__c,
      formName: 'cem-posventa',
      formType: 'otherform',
      pageCategory: 'Encuesta',
      name: idPerson.FirstName,
      surname: idPerson.LastName,
      secondSurname: idPerson.TXT_2_Surname__c,
      concesion: dealerName,
    }, // Analítica
  };

  const sendForm = (): Promise<boolean> => {
    const { formData }: OneFormState = store.getState();
    const { fields, headers } = formData;

    return new Promise<any>(async (resolve, reject) => {
      const { tc, today, url, device, location } = getParamsForm();

      const data = {
        ...formatHeaders(headers),
        'headers[COD_FORM_ID]': `vw-oneform-${fields.formName}`,
        'headers[DESC_DEVICETYPE]': device,
        'headers[DESC_ID_CLIMAPRO]': idPerson.ID_Person_DIM_IMP__c,
        'headers[DESC_ID_VEHICLE_CLIMAPRO]': idVehicle.IDClimapro__c,
        'headers[DESC_URL]': location,
        'headers[DESC_URLORIGIN]': url,
        'headers[FECHA_INSERTION_DATE]': today,
        'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
        'headers[TC]': tc,

        'flags[FLAG_CONF_DATA]': fields.datosCorrectos ? fields.datosCorrectos : fields.datosIncorrectos ? 'false' : 'true',

        'fields[DAT_REGISTRATION_DATE]': dpsData.dat,
        'fields[DESC_ACTUAL_VEHICLE_MODEL]': fields.modelo ? fields.modelo : null,
        'fields[COD_ACTUAL_VEHICLE_MODEL]': idVehicle.DESC_Model__c ? idVehicle.DESC_Model__c : dpsData.model ? dpsData.model : null,
        'fields[DESC_ADDRESS]': idPerson.FORM_Full_Address__c,
        'fields[DESC_ANONYMOUS]': fields.enviarConcesion,
        'fields[DESC_AUXFIELD2]': fields.publcarRespuestas === '1' ? 'PERMITE' : 'NO PERMITE',
        'fields[DESC_DEALER_CODE_KVPS]': dealerGet.kvps,
        'fields[DESC_DEALER_CODE]': dpsData.dealer,
        'fields[DESC_DEALER]': dealerName,
        'fields[DESC_EMAIL_ADDRESS]': idPerson.TXT_Email__c,
        'fields[DESC_EMPLOYEE_ID]': adviser,
        'fields[DESC_FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,
        'fields[DESC_FRAME]': idVehicle.Frame__c,
        'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
        'fields[DESC_LOPD]': 'PERMITE',
        'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
        'fields[DESC_NAME]': fields.name,
        'fields[DESC_NUMBER_ORDER]': dpsData.or,
        'fields[DESC_POSTAL_CODE]': idPerson.TXT_Postal_Code__c,
        'fields[DESC_PROVINCE]': idPerson.TXT_Province__c,
        'fields[DESC_SURNAME_1]': fields.surname,
        'fields[DESC_SURNAME_2]': fields.secondSurname,
        'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO',
        'fields[DESC_VEH_PLATE]': idVehicle.Registration__c,
        'fields[DESC_WRONG_DATA]': handleDescWrongData(fields.datosIncorrectosPosventa),
        'fields[NUM_PHONE]': idPerson.NUM_Phone_mobile__c,

        'fields[CEM_Q01]': formatRatingField(fields.satisfaccionTrato),
        'fields[CEM_C01]': fields.satisfaccionTratoMotivo,
        'fields[CEM_Q02]': formatRatingField(fields.satisfaccionOrganizacion),
        'fields[CEM_C02]': fields.satisfaccionOrganizacionMotivo,
        'fields[CEM_Q03]': formatRatingField(fields.satisfaccionTrabajoRealizado),
        'fields[CEM_C03]': fields.satisfaccionTrabajoRealizadoMotivo,
        'fields[CEM_Q04]': formatRatingField(fields.satisfaccionConcesion),
        'fields[CEM_C04]': fields.satisfaccionConcesionMotivo,
        'fields[CEM_C05]': fields.aspectoSatisfaccion,
        'fields[CEM_Q06]': formatRatingField(fields.satisfaccionVehiculo),
        'fields[CEM_C06]': fields.satisfaccionVehiculoMotivo,
        'fields[CEM_Q07]': fields.motivoVisita?.split(',').sort((a, b) => a - b).join(''),
        'fields[CEM_Q12]': formatRatingField(fields.tiempoSatisfaccion),
        'fields[CEM_Q13]': fields.alternativaTransporte,
        'fields[CEM_Q30]': formatBooleanField(fields.informado),

      };

      if (!fields.datosIncorrectosPosventa || fields.datosCorrectos == "true") {
        delete data['fields[DESC_WRONG_DATA]'];
      }

      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);
      checkUndefinedsInData(data, 'Posventa');
      const response = await dpsSend(formData);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  /**
   * Se extrae la lógica para poder utilizarla por separado sin tener que usar las funciones de loading o updateSteps
   * ya que hay una pregunta en la que podemos eliminar y añadir un step en el mismo paso.
   * @param stepsToAdd
   * @param alternativeSteps
   * @returns
   */
  const addingStepsLogic = (stepsToAdd: string[], alternativeSteps?: Steps[]) => {
    const { steps: actualSteps, screenIndex } = store.getState().formInfo;
    const stepsToUse = alternativeSteps ? alternativeSteps : actualSteps;
    // Hay que revertir los steps para que se añadan en el orden correcto
    const indexesInSteps = stepsToAdd.reverse().map((step) => getIndexInSteps(stepsToUse, step));
    let newSteps = [...stepsToUse];

    indexesInSteps.forEach((index, i) => {
      if (index === -1) {
        newSteps = addRemovedStep(stepsPosventa, stepsToUse, stepsToAdd[i], screenIndex);
      }
    });
    const finalSteps = resetStepsScreenIndex(newSteps);
    return finalSteps;
  };

  const addSteps = (stepsToAdd: string[]) => {
    setLoading(true);

    const finalSteps = addingStepsLogic(stepsToAdd);
    updateSteps(finalSteps);
    handleNextStep();

    setLoading(false);
  };

  const removeSteps = (stepsToDelete: string[]) => {
    const { steps } = store.getState().formInfo;
    setLoading(true);
    const finalSteps = removeMultipleStepsAndResetScreenIndex(steps, stepsToDelete);
    updateSteps(finalSteps);
    setLoading(false);
  };

  const updateSteps = (steps: Steps[]) => {
    dispatch({ type: 'UPDATE_STEPS', payload: steps });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: steps[steps.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: steps.length });
  };

  const replaceWithLeadInfo = (string: string) => {
    const aux = idVehicle.DESC_Model__c ? idVehicle.DESC_Model__c : dpsData.model ? dpsData.model : 'Volkswagen';
    return string
      .replace('[Modelo]', aux)
      .replace('[Marca]', idVehicle.PKL_order_brand__c)
      .replace('[Concesionario]', dealerName);
  };

  const replaceModelo = (string: string) => {
    if (modeloRef.current) {
      return string.replace(modeloRef.current, modelo);
    } else {
      return string.replace(idVehicle.DESC_Model__c, modelo);
    }
  };

  const modifyTitleSteps = (steps: Steps[], replaceCallback: (s: string) => string) => {
    return steps.map((step) => {
      if (!step.title) return step;
      if (Array.isArray(step.title.props.children)) {
        step.title.props.children = step.title.props.children.map((child: any) => {
          if (typeof child === 'object') {
            if (typeof child.props.children === 'object') {
              child.props.children.props.children = replaceCallback(child.props.children.props.children);
            } else {
              child.props.children = replaceCallback(child.props.children);
            }
          } else {
            child = replaceCallback(child);
          }
          return child;
        });
      } else if (typeof step.title.props.children === 'object') {
        step.title.props.children.props.children = replaceCallback(step.title.props.children.props.children);
      } else {
        step.title.props.children = replaceCallback(step.title.props.children);
      }
      return step;
    });
  };

  const getOrderStep = (order: number, sc4: number, sc5: number, sc6: number) => {
    return order === 1 ? sc4 : order === 2 ? sc5 : order === 3 ? sc6 : null;
  };

  const shuffle = (array: number[]): number[] => array.sort(() => Math.random() - 0.5);

  /**
   * El orden de las preguntas Sc_4,Sc_5 y Sc_6 tiene que ser aleatorio.
   * @param steps
   */
  const randomizeSteps = (steps: Steps[]): Steps[] => {
    const sc4 = getIndexInSteps(steps, 'SatisfaccionTrato');
    const sc4Motivo = getIndexInSteps(steps, 'SatisfaccionTratoMotivo');

    const sc5 = getIndexInSteps(steps, 'SatisfaccionOrganizacion');
    const sc5Motivo = getIndexInSteps(steps, 'SatisfaccionOrganizacionMotivo');

    const sc6 = getIndexInSteps(steps, 'SatisfaccionTrabajoRealizado');
    const sc6Motivo = getIndexInSteps(steps, 'SatisfaccionTrabajoRealizadoMotivo');

    let newSteps = [...steps];
    const [first, second, third] = shuffle([1, 2, 3]);
    const firstStep = getOrderStep(first, sc4, sc5, sc6);
    const secondStep = getOrderStep(second, sc4, sc5, sc6);
    const thirdStep = getOrderStep(third, sc4, sc5, sc6);
    newSteps[sc4] = steps[firstStep];
    newSteps[sc4Motivo] = steps[firstStep + 1];
    newSteps[sc5] = steps[secondStep];
    newSteps[sc5Motivo] = steps[secondStep + 1];
    newSteps[sc6] = steps[thirdStep];
    newSteps[sc6Motivo] = steps[thirdStep + 1];
    return newSteps;
  };

  const getFormInfo = (steps: Steps[]) => {
    const newSteps = modifyTitleSteps(steps, replaceWithLeadInfo);
    const randomizedSteps = randomizeSteps(newSteps);

    return {
      firstStep: randomizedSteps[0],
      lastStep: randomizedSteps[randomizedSteps.length - 1],
      stepsHistory: [0],
      numberOfScreens: randomizedSteps.length,
      newSteps: randomizedSteps,
    };
  };

  //
  // Lógica para modificar el flujo del formulario
  //

  const handleRatingStars = (value: string, nameOfMotivoStep: string) => {
    if (betweenOneAndThreeStars(value)) {
      addSteps([nameOfMotivoStep]);
    } else if (fourOrFiveStars(value) || noRatingStars(value)) {
      removeSteps([nameOfMotivoStep]);
    }
  };

  useEffect(() => {
    if (modelo) {
      const newSteps = modifyTitleSteps(stepsPosventa, replaceModelo);
      modeloRef.current = modelo;
      updateSteps(newSteps);
    }
  }, [modelo]);

  useEffect(() => {
    if (datosCorrectos === 'true') {
      const steps = store.getState().formInfo.steps;
      const modelo = getIndexInSteps(steps, 'Modelo');
      // En el caso de elegir que los datos son incorrectos, llegar a confirmación y volver atrás hay que regenerar todos los pasos.
      if (modelo === -1) {
        const initialSteps = store.getState().formInfo.initialSteps;
        const finalSteps = removeMultipleStepsAndResetScreenIndex(initialSteps, ['DatosIncorrectosPosventa', 'Modelo']);
        updateSteps(finalSteps);
      } else {
        removeSteps(['DatosIncorrectosPosventa', 'Modelo', 'ConfirmacionConcesion']);
      }
    } else if (datosCorrectos === 'false') {
      addSteps(['DatosIncorrectosPosventa', 'Modelo']);
    }
  }, [datosCorrectos]);

  useEffect(() => {
    if (
      datosIncorrectosPosventa === 'Marca' ||
      datosIncorrectosPosventa === 'Instalación' ||
      datosIncorrectosPosventa === 'Contacto'
    ) {
      const pastSteps = store.getState().formInfo.steps;
      const newsteps = [pastSteps[0], pastSteps[1], pastSteps[pastSteps.length - 1]];
      const finalSteps = resetStepsScreenIndex(newsteps);
      updateSteps(finalSteps);
    }
    if (datosIncorrectosPosventa === 'Modelo') {
      const initialSteps = store.getState().formInfo.initialSteps;
      const finalSteps = resetStepsScreenIndex(initialSteps);
      updateSteps(finalSteps);
    }
  }, [datosIncorrectosPosventa]);

  useEffect(() => {
    // Mostrar o no Sc. 4_1
    if (!satisfaccionTrato) {
      return;
    }
    handleRatingStars(satisfaccionTrato, 'SatisfaccionTratoMotivo');
  }, [satisfaccionTrato]);

  useEffect(() => {
    if (!satisfaccionOrganizacion) {
      return;
    }
    // Mostrar o no Sc. 5_1
    handleRatingStars(satisfaccionOrganizacion, 'SatisfaccionOrganizacionMotivo');
  }, [satisfaccionOrganizacion]);

  useEffect(() => {
    if (!satisfaccionTrabajoRealizado) {
      return;
    }
    // Mostrar o no Sc. 6_1
    handleRatingStars(satisfaccionTrabajoRealizado, 'SatisfaccionTrabajoRealizadoMotivo');
  }, [satisfaccionTrabajoRealizado]);

  useEffect(() => {
    if (!enviarConcesion) {
      return;
    }
    if (enviarConcesion === '2' || enviarConcesion === '0') {
      removeSteps(['DatosPersonales']);
      //   dispatch({ type: 'UPDATE_FORM_ENDED', payload: true });
      // handleShowFinalScreen();
    } else if (enviarConcesion === '1') {
      addSteps(['DatosPersonales']);
    }
  }, [enviarConcesion]);

  useEffect(() => {
    if (!masPreguntas) {
      return;
    }

    if (masPreguntas === 'true') {
      const stepNames = getArrayOfStepsNames(stepsPosventa, 'MasPreguntas', 'EnviarConcesion');
      addSteps(stepNames);
    } else if (masPreguntas === 'false') {
      const { steps } = store.getState().formInfo;
      const stepNames = getArrayOfStepsNames(steps, 'MasPreguntas', 'EnviarConcesion');
      removeSteps(stepNames);
    }
  }, [masPreguntas]);

  /**
   * Se tiene que de manera consecutiva y no mediante las otras funciones porque si no, se actualizan los steps
   * dos veces y en el segundo paso (añadir o eliminar el otro step) se hace siempre sobre los steps antiguos y
   * no funciona de la manera esperada.
   *
   * @param stepToRemove
   * @param stepToAdd
   */
  const removeAndAddStepAtTheSameTime = (stepToRemove: string, stepToAdd: string) => {
    const { steps } = store.getState().formInfo;
    setLoading(true);
    const removedSteps = removeMultipleStepsAndResetScreenIndex(steps, [stepToRemove]);
    const addedSteps = addingStepsLogic([stepToAdd], removedSteps);
    const finalSteps = resetStepsScreenIndex(addedSteps);
    handleNextStep();
    updateSteps(finalSteps);
    setLoading(false);
  };

  useEffect(() => {
    if (!satisfaccionTrato || !satisfaccionOrganizacion || !satisfaccionTrabajoRealizado) {
      return;
    }

    let removeSatisfaccionConcesion = null;
    let removeAspectoSatisfaccion = null;
    // Mostrar o no Sc. 7_1
    removeSatisfaccionConcesion = !showSatisfaccionConcesion(
      satisfaccionConcesion,
      satisfaccionTrato,
      satisfaccionOrganizacion,
      satisfaccionTrabajoRealizado
    );
    // Mostrar o no Sc. 7_2
    removeAspectoSatisfaccion = !showOtroAspecto(
      satisfaccionConcesion,
      satisfaccionTrato,
      satisfaccionOrganizacion,
      satisfaccionTrabajoRealizado
    );

    if (!removeSatisfaccionConcesion && !removeAspectoSatisfaccion) {
      addSteps(['AspectoSatisfaccionMotivo', 'SatisfaccionConcesionMotivo']);
    }
    if (removeSatisfaccionConcesion && removeAspectoSatisfaccion) {
      removeSteps(['AspectoSatisfaccionMotivo', 'SatisfaccionConcesionMotivo']);
    }
    if (!removeSatisfaccionConcesion && removeAspectoSatisfaccion) {
      removeAndAddStepAtTheSameTime('AspectoSatisfaccionMotivo', 'SatisfaccionConcesionMotivo');
    }
    if (removeSatisfaccionConcesion && !removeAspectoSatisfaccion) {
      removeAndAddStepAtTheSameTime('SatisfaccionConcesionMotivo', 'AspectoSatisfaccionMotivo');
    }
  }, [satisfaccionConcesion]);

  useEffect(() => {
    const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } = getFormInfo(stepsPosventa);

    const NuevoFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      screenIndex: firstStep.screenIndex,
      step: firstStep,
      steps: newSteps ? newSteps : stepsPosventa,
      stepsHistory,
      customLegal: idPerson.LSSI__c === 'PERMITE' ? <CustomLegalCem /> : <CEMLegal copy={'Servicio Oficial'} />,
      hideLegal: idPerson.LSSI__c === 'PERMITE' ? true : false,
      thankYouComponent: <ThankYouCEM />,
    };

    initializeForm(NuevoFormInfo, NuevoFormData);
  }, []);

  return !loading ? <FormController steps={stepsPosventa} screenType="full-screen" /> : null;
}
